import SentryService from '@services/SentryService';
import { raceWithTimeout } from '@utils/raceWithTimeout';

import type { DeviceTransport } from './types';

/**
 * Wall-clock cap on a single `getUserMedia` call.
 *
 * A Bluetooth capture handoff (the HFP profile switch) completes within a
 * few seconds when it completes at all. The failure this guards is the call
 * that never settles (GDN-3273): the recorder sat in "Starting…" for
 * 10–15 minutes with Start Recording disabled and no chunk ever captured.
 */
export const DEFAULT_MIC_ACQUIRE_TIMEOUT_MS = 10_000;

/**
 * Which acquisition a call belongs to. `exact` and `default` are the
 * StreamAcquirer's two attempts; `raw` is a RecordingContext call that
 * bypasses the device service; `permission` is the throwaway prompt call;
 * `call_track` is the echo-cancelled JotMeet capture.
 */
export type MicAcquireAttempt = 'exact' | 'default' | 'raw' | 'permission' | 'call_track';

/**
 * What the clinician can do about a microphone that never started. Shared by
 * the Start Recording toast and the permission overlay so both say the same
 * thing. "Reload" is there because Chromium serialises getUserMedia calls per
 * frame: a retry in the same tab can queue behind the wedged request.
 */
export const MIC_CONTENTION_MESSAGE =
  "Couldn't start the microphone. Make sure it isn't in use by another tab or app, then try again. If it keeps happening, reload this tab.";

export interface GetUserMediaWithTimeoutOptions {
  attempt: MicAcquireAttempt;
  timeoutMs?: number;
  /**
   * Sentry level for the timeout report: `warning` when the caller falls
   * back and may still recover the session, `error` (default) otherwise.
   */
  severity?: 'warning' | 'error';
  /** Diagnostic context carried on the timeout error and the Sentry event. */
  requestedDeviceId?: string;
  requestedTransport?: DeviceTransport;
}

/**
 * Thrown when `getUserMedia` does not settle within the cap.
 *
 * Kept distinct from the DOMException family so callers can tell "the
 * browser refused" from "the browser never answered".
 */
export class MicrophoneAcquireTimeoutError extends Error {
  readonly timeoutMs: number;
  readonly attempt: MicAcquireAttempt;
  readonly requestedDeviceId?: string;
  readonly requestedTransport?: DeviceTransport;

  constructor(context: {
    timeoutMs: number;
    attempt: MicAcquireAttempt;
    requestedDeviceId?: string;
    requestedTransport?: DeviceTransport;
  }) {
    super(`getUserMedia (${context.attempt}) did not settle within ${context.timeoutMs}ms`);
    this.name = 'MicrophoneAcquireTimeoutError';
    this.timeoutMs = context.timeoutMs;
    this.attempt = context.attempt;
    this.requestedDeviceId = context.requestedDeviceId;
    this.requestedTransport = context.requestedTransport;
  }
}

/**
 * `navigator.mediaDevices.getUserMedia` bounded by a wall-clock cap.
 *
 * The browser call cannot be cancelled. A stream that arrives after the cap
 * would hold the microphone open with no owner, so it is stopped on arrival.
 *
 * A hung call throws nothing on its own, which is why this failure mode was
 * invisible in Sentry. The timeout is reported here, the one place every
 * acquisition path goes through, so it stays visible whether or not a
 * fallback later recovers the session. Reports are fingerprinted per attempt
 * kind so Sentry shows one issue per kind rather than one per call site.
 */
export async function getUserMediaWithTimeout(
  constraints: MediaStreamConstraints,
  options: GetUserMediaWithTimeoutOptions,
): Promise<MediaStream> {
  const timeoutMs = options.timeoutMs ?? DEFAULT_MIC_ACQUIRE_TIMEOUT_MS;
  const request = navigator.mediaDevices.getUserMedia(constraints);
  const result = await raceWithTimeout(request, timeoutMs);
  if (!result.timedOut) return result.value;

  void request.then(releaseOrphanedStream, () => undefined);

  const error = new MicrophoneAcquireTimeoutError({
    timeoutMs,
    attempt: options.attempt,
    requestedDeviceId: options.requestedDeviceId,
    requestedTransport: options.requestedTransport,
  });
  SentryService.captureException(error, {
    level: options.severity ?? 'error',
    tags: {
      component: 'getUserMediaWithTimeout',
      attempt: options.attempt,
      requested_transport: options.requestedTransport ?? 'unknown',
    },
    extra: { requestedDeviceId: options.requestedDeviceId, timeoutMs },
    fingerprint: ['mic-acquire-timeout', options.attempt],
  });
  throw error;
}

function releaseOrphanedStream(stream: MediaStream): void {
  stream.getTracks().forEach(track => track.stop());
  SentryService.addBreadcrumb({
    category: 'audio.stream',
    message: 'Released a microphone stream that arrived after the acquire timeout',
    level: 'warning',
  });
}
